/**
 * Thin wrapper around the /optimizer/* endpoints.
 *
 * Four endpoints are live and role-gated to COORDINATOR_PLUS_ROLES:
 *   POST /optimizer/plan
 *   GET /optimizer/coverage
 *   POST /optimizer/plan/confirm (currently 501 stub)
 *   GET /optimizer/plan/history (currently returns empty list stub)
 *
 * Every function returns shapes declared here, which mirror the backend
 * Pydantic schemas from backend/app/schemas/optimizer.py.
 */
import client from '@/api/client';

// ============================================================================
// Phase A — Live Endpoints
// ============================================================================

/**
 * Tier breakdown for a turf candidate.
 * Maps tier names (e.g., "anchor", "hot") to their counts.
 */
export interface TierCounts {
  [tierName: string]: number;
}

/**
 * A ranked turf candidate returned by POST /optimizer/plan.
 */
export interface TurfCandidate {
  route_id: number;
  route_name: string;
  precinct_num: string | null;
  subregion: string | null;
  door_count: number;
  voter_count: number;
  tier_counts: TierCounts;
  expected_contacts: number;
  expected_ballots_moved: number;
  priority_score: number;
  under_contacted: boolean;
}

/**
 * Request body for POST /optimizer/plan.
 */
export interface PlanRequest {
  election_date: string; // ISO "2026-08-04"
  election_type: string; // "primary" | "general"
  available_shifts: number; // 1..500
  prefer_anchor_heavy?: boolean;
  excluded_precincts?: string[];
}

/**
 * Response from POST /optimizer/plan.
 */
export interface PlanResponse {
  election_date: string;
  election_type: string;
  available_shifts: number;
  assumed_contact_rate: number;
  turfs: TurfCandidate[];
  total_expected_ballots_moved: number;
  total_doors: number;
  generated_at: string; // ISO timestamp
}

/**
 * Per-precinct coverage row returned by GET /optimizer/coverage.
 */
export interface PrecinctCoverageRow {
  precinct_num: string;
  voter_count: number;
  contacted_count: number;
  contact_rate: number;
  expected_ballots: number;
  under_contacted: boolean;
}

/**
 * Response from GET /optimizer/coverage.
 */
export interface CoverageResponse {
  since: string; // ISO date
  precincts: PrecinctCoverageRow[];
  tenant_avg_contact_rate: number;
}

// ============================================================================
// Phase B — Stub Endpoints (Agent A building in parallel)
// ============================================================================

/**
 * A single turf assignment within a plan confirmation.
 */
export interface PlanConfirmTurf {
  route_id: number;
  canvasser_id: number;
  predicted_votes_moved: number;
  planned_start_at?: string | null; // Optional ISO datetime
}

/**
 * Request body for POST /optimizer/plan/confirm.
 */
export interface PlanConfirmRequest {
  election_date: string;
  election_type: string;
  turfs: PlanConfirmTurf[];
  notes?: string | null;
}

/**
 * Response from POST /optimizer/plan/confirm (Phase B).
 */
export interface PlanConfirmResponse {
  plan_id: string;
  shifts_created: number;
  shifts_updated: number;
  total_predicted_votes_moved: number;
  planned_start_at: string; // ISO datetime
  created_shift_ids: number[];
}

/**
 * A single row in the plan history.
 */
export interface PlanHistoryRow {
  plan_id: string;
  planned_start_at: string; // ISO date
  shift_count: number;
  total_predicted_votes_moved: number;
  routes_covered: number;
  canvassers_assigned: number;
}

/**
 * Response from GET /optimizer/plan/history.
 */
export interface PlanHistoryResponse {
  plans: PlanHistoryRow[];
  total: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * POST /optimizer/plan
 * Generate an optimized plan given election parameters and shift budget.
 */
export async function generatePlan(
  body: PlanRequest,
): Promise<PlanResponse> {
  const res = await client.post<PlanResponse>('/optimizer/plan', body);
  return res.data;
}

/**
 * GET /optimizer/coverage
 * Fetch precinct-level coverage statistics.
 * Optionally accepts query params: election_date, election_type.
 */
export async function getCoverage(queryParams?: {
  election_date?: string;
  election_type?: string;
}): Promise<CoverageResponse> {
  const res = await client.get<CoverageResponse>(
    '/optimizer/coverage',
    queryParams ? { params: queryParams } : undefined,
  );
  return res.data;
}

/**
 * Filters a coverage screen may be narrowed by. Every field optional, and the
 * names are the query params `/optimizer/coverage` already takes — this is a
 * pass-through, not a second filter vocabulary to keep in sync.
 */
export interface CoverageFilters {
  since?: string | null;
  precinct_num?: string | null;
  subregion?: string | null;
  legislative_district?: string | null;
  under_contacted?: boolean | null;
}

/**
 * The worst-covered precincts in a filtered set, worst first.
 *
 * Backs the "Cover the gaps" Easy button (memory-bank/plans/easy-buttons.md
 * §3 #2), which feeds these precinct numbers straight into autoplan's
 * `precinct_nums`.
 *
 * ⚠️ This asks the server again rather than reading the rows already on the
 * table, and the extra round trip is the point: the table is sortable and
 * paginated, so "the rows in front of you" can be page 3 of a voter-count
 * sort. Taking the worst N from THAT would plan routes for precincts that are
 * not the worst anything, and it would do it silently. `sort=contact_rate&
 * order=asc` re-derives worst-first across the whole filtered set, so the
 * button means what its label says no matter what the user was looking at.
 *
 * Filters are carried through: a coordinator who narrowed to LD 12 gets LD 12's
 * worst, not the county's.
 */
export async function fetchWorstCoveredPrecincts(
  worstN: number,
  filters: CoverageFilters = {},
  signal?: AbortSignal,
): Promise<PrecinctCoverageRow[]> {
  const params: Record<string, unknown> = {
    envelope: 'v2',
    sort: 'contact_rate',
    order: 'asc',
    page: 1,
    page_size: worstN,
  };
  for (const [key, value] of Object.entries(filters)) {
    if (value !== null && value !== undefined && value !== '') {
      params[key] = value;
    }
  }
  const res = await client.get('/optimizer/coverage', { params, signal });
  // envelope=v2 pages under `items`; the v1 shape is still what a bare call
  // returns, so accept either rather than depending on a query param the
  // server could stop honouring.
  const data = res.data as { items?: PrecinctCoverageRow[]; precincts?: PrecinctCoverageRow[] };
  return data?.items ?? data?.precincts ?? [];
}

/**
 * POST /optimizer/plan/confirm
 * Confirm a generated plan and create canvass shifts.
 * Currently a 501 stub — will be implemented by Agent A.
 */
export async function confirmPlan(
  body: PlanConfirmRequest,
): Promise<PlanConfirmResponse> {
  const res = await client.post<PlanConfirmResponse>(
    '/optimizer/plan/confirm',
    body,
  );
  return res.data;
}

/**
 * GET /optimizer/plan/history
 * Fetch the list of previously confirmed plans.
 * Currently returns an empty list stub — will be implemented by Agent A.
 */
export async function getPlanHistory(limit: number = 10): Promise<PlanHistoryResponse> {
  const res = await client.get<PlanHistoryResponse>(
    '/optimizer/plan/history',
    { params: { limit } },
  );
  return res.data;
}

// ============================================================================
// Error Helpers
// ============================================================================

/**
 * Format an axios error into a user-facing message for the optimizer view.
 */
export function formatOptimizerError(err: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  const status = e?.response?.status as number | undefined;
  const detail = e?.response?.data?.detail as string | undefined;
  if (status === 401) {
    return 'Not authenticated. Log in and try again.';
  }
  if (status === 403) {
    return 'You need coordinator access to use the optimizer.';
  }
  if (status === 404) {
    return detail || 'Not found.';
  }
  if (status === 409) {
    return detail || 'This operation is not valid in the current state.';
  }
  if (status === 422) {
    return detail || 'Invalid input.';
  }
  if (!status) {
    return 'Could not reach the backend.';
  }
  return detail || `Backend error ${status}.`;
}
